import { zip, strToU8 } from 'fflate';
import { Session } from '../models/session';
import { ShotData } from '../models/shotData';
import { Swing } from '../models/swing';
import { swingFeelLabel } from './swing-result';

export function buildSwingMarkdown(session: Session | null, swing: Swing): string {
  const sessionLabel = session?.label?.trim() || 'Practice session';
  const dateLine = formatSessionDate(session?.date);
  const tags = (swing.tags ?? []).map(tag => tag.trim()).filter(Boolean);
  const notes = (swing.notes ?? '').trim() || 'None';
  const videos: string[] = [];
  if ((swing.faceOnUrl ?? '').trim()) {
    videos.push(`- Face-on: ${videoFileName(swing.faceOnUrl, 'face-on')}`);
  }
  if ((swing.dtlUrl ?? '').trim()) {
    videos.push(`- Down the line: ${videoFileName(swing.dtlUrl, 'down-the-line')}`);
  }

  const lines = [
    `# Swing ${swing.sequence} — ${sessionLabel}`,
    dateLine ? `Date: ${dateLine}` : '',
    '',
    'This is golf practice video from two camera angles plus my notes.',
    '',
    '## Videos',
    videos.length ? videos.join('\n') : '- No videos attached',
    '',
    '## Feel',
    swingFeelLabel(swing.result),
    '',
    '## Tags',
    tags.length ? tags.join(', ') : 'None',
    '',
    '## Notes',
    notes,
    '',
    '## Shot data',
    shotDataMarkdown(swing.shotData)
  ];

  const startLines = startTimeSection(swing);
  if (startLines) {
    lines.push('', '## Swing start', startLines);
  }

  return lines.filter((line, index, all) => !(line === '' && all[index - 1] === '')).join('\n').trim() + '\n';
}

export function swingExportZipName(session: Session | null, swing: Swing): string {
  const slug = slugify(session?.label || 'session');
  return `swing-${swing.sequence}-${slug}.zip`;
}

export function videoFileName(url: string, stem: 'face-on' | 'down-the-line'): string {
  const ext = extensionFromUrl(url);
  return `${stem}${ext}`;
}

export function zipSwingPackage(files: Record<string, Uint8Array>): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    zip(files, { level: 0 }, (err, data) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(data);
    });
  });
}

export function markdownToBytes(markdown: string): Uint8Array {
  return strToU8(markdown);
}

async function blobToBytes(blob: Blob): Promise<Uint8Array> {
  return new Uint8Array(await blob.arrayBuffer());
}

export { blobToBytes };

function shotDataMarkdown(shotData: ShotData | null): string {
  if (!shotData) {
    return 'None';
  }

  const rows = Object.values(shotData)
    .filter(field => field && field.value !== null && field.value !== '')
    .map(field => `| ${field.label} | ${field.value} |`);

  if (rows.length === 0) {
    return 'None';
  }

  return ['| Metric | Value |', '| --- | --- |', ...rows].join('\n');
}

function startTimeSection(swing: Swing): string {
  const parts: string[] = [];
  if (swing.faceOnStartTime != null && isFinite(swing.faceOnStartTime)) {
    parts.push(`- Face-on swing starts at ${swing.faceOnStartTime.toFixed(2)}s`);
  }
  if (swing.dtlStartTime != null && isFinite(swing.dtlStartTime)) {
    parts.push(`- Down the line swing starts at ${swing.dtlStartTime.toFixed(2)}s`);
  }
  return parts.join('\n');
}

function formatSessionDate(date: Date | string | undefined): string {
  if (!date) {
    return '';
  }
  const parsed = date instanceof Date ? date : new Date(date);
  if (isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

function slugify(value: string): string {
  const slug = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  return slug || 'session';
}

function extensionFromUrl(url: string): string {
  const match = url.match(/\.(mp4|webm|mov)(?:[?#]|$)/i);
  return match ? `.${match[1].toLowerCase()}` : '.mp4';
}
